/**
 * Google Analytics 4 (GA4) setup via react-ga4.
 * Loads only after the user opts in to analytics cookies (Google Consent Mode v2).
 *
 * Business funnel (GA4 already reports these by source, medium, campaign,
 * landing page, device, country, and new versus returning):
 * 1. Organic landing session — default session attribution on page_view
 * 2. view_features
 * 3. begin_business_registration
 * 4. business_registration_complete
 * 5. first_business_setup_complete — not sent until the setup milestone is confirmed
 *
 * Tenant pages such as /sunflowernails/ stay separate because they are different page paths.
 */
import ReactGA from 'react-ga4';
import { hasAnalyticsConsent } from './cookieConsent';

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

let initialized = false;
let defaultConsentApplied = false;

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

function pushGtagCommand(...args: unknown[]): void {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
}

/**
 * Set Consent Mode defaults before GA loads. Required for GDPR-compliant GA4.
 * Call once at app bootstrap, before any analytics initialization.
 */
export function setDefaultConsentDenied(): void {
  if (defaultConsentApplied || typeof window === 'undefined') return;

  pushGtagCommand('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });

  defaultConsentApplied = true;
}

function updateGtagConsent(analyticsGranted: boolean): void {
  const status = analyticsGranted ? 'granted' : 'denied';

  if (initialized) {
    ReactGA.gtag('consent', 'update', {
      analytics_storage: status,
      ad_storage: status,
      ad_user_data: status,
      ad_personalization: status,
    });
    return;
  }

  pushGtagCommand('consent', 'update', {
    analytics_storage: status,
    ad_storage: status,
    ad_user_data: status,
    ad_personalization: status,
  });
}

/**
 * Initialize GA4 once analytics consent is granted. Safe to call multiple times.
 */
export function initGA(): void {
  if (initialized || !GA_MEASUREMENT_ID || !hasAnalyticsConsent()) return;

  try {
    ReactGA.initialize(GA_MEASUREMENT_ID, {
      gtagOptions: {
        send_page_view: false,
      },
    });
    initialized = true;
    updateGtagConsent(true);
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[analytics] GA init failed:', e);
    }
  }
}

/**
 * Apply the user's analytics cookie preference to Consent Mode and GA loading.
 */
export function applyAnalyticsConsent(granted: boolean): void {
  if (granted) {
    initGA();
    if (initialized) {
      updateGtagConsent(true);
    }
    return;
  }

  updateGtagConsent(false);
}

/**
 * Send a page_view for the current path. Call this on every route change.
 */
export function sendPageView(path: string, title?: string): void {
  if (!initialized || !hasAnalyticsConsent()) return;

  try {
    ReactGA.send({
      hitType: 'pageview',
      page: path,
      title: title ?? document.title,
    });
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[analytics] sendPageView failed:', e);
    }
  }
}

function isTestAnalyticsHost(): boolean {
  if (typeof window === 'undefined') return true;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || navigator.webdriver === true;
}

function isInternalTeamUser(): boolean {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return false;
    const email = String(JSON.parse(raw)?.email || '').trim().toLowerCase();
    return email.endsWith('@aainomatic.com');
  } catch {
    return false;
  }
}

export type BusinessFunnelEvent =
  | 'view_features'
  | 'begin_business_registration'
  | 'business_registration_complete'
  | 'first_business_setup_complete';

const lastPageViewEventAt = new Map<string, number>();

/**
 * Funnel events for business-account measurement.
 * Page views fire once per load. Conversions are deduped for the browser tab
 * so a refresh or retry is not counted again. Event params must stay free of
 * names, email addresses, phone numbers, and other personal details.
 */
export function sendBusinessFunnelEvent(
  action: BusinessFunnelEvent,
  params?: Record<string, string>
): void {
  if (!initialized || !hasAnalyticsConsent() || isTestAnalyticsHost() || isInternalTeamUser()) return;

  if (action === 'view_features') {
    const now = Date.now();
    const previous = lastPageViewEventAt.get(action) || 0;
    if (now - previous < 1000) return;
    lastPageViewEventAt.set(action, now);
  } else {
    const key = `aacore-funnel:${action}`;
    try {
      if (sessionStorage.getItem(key) === '1') return;
      sessionStorage.setItem(key, '1');
    } catch {
      // sessionStorage may be unavailable; still send the event once this load.
    }
  }

  try {
    ReactGA.event(action, {
      category: 'business_funnel',
      ...params,
    });
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[analytics] sendBusinessFunnelEvent failed:', e);
    }
  }
}

/**
 * Bootstrap analytics based on a previously saved consent choice.
 */
export function bootstrapAnalyticsFromStoredConsent(): void {
  setDefaultConsentDenied();
  if (hasAnalyticsConsent()) {
    initGA();
  }
}
